import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import CategoryFeed from "@/pages/category/components/CategoryFeed";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { SITE_NAME, SITE_URL } from "@/utils/format";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const list = articles.filter((item) => item.category === slug);

  const path = `/category/${slug}`;
  const jsonLd = category
    ? [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name} - ${SITE_NAME}`,
          description: category.intro,
          url: `${SITE_URL}${path}`,
          inLanguage: "zh-CN",
          isPartOf: {
            "@type": "WebSite",
            name: SITE_NAME,
            url: `${SITE_URL}/`,
          },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category.name,
              item: `${SITE_URL}${path}`,
            },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${category.name}爆料列表`,
          itemListElement: list.map((article, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: article.title,
            url: `${SITE_URL}/article/${article.id}`,
          })),
        },
      ]
    : undefined;

  useSeo({
    title: category
      ? `${category.name}爆料 - 瓜友会网 | 实时更新吃瓜现场`
      : `瓜田不存在 - ${SITE_NAME}`,
    description: category
      ? `${category.name}是瓜友会网的常驻瓜田：${category.desc}。实时更新相关爆料与网友热评，蹲最新进展就来瓜友会网。`
      : "你访问的瓜田暂时不存在，返回瓜友会网首页继续吃瓜。",
    keywords: category
      ? `瓜友会网,${category.name},${category.name}爆料,吃瓜现场,实时吃瓜`
      : "瓜友会网,吃瓜现场",
    path,
    jsonLd,
  });

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        {category ? (
          <>
            <div className="w-full px-4 md:px-8 py-4 md:py-5 bg-background-100">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: category.name },
                ]}
              />
            </div>
            <CategoryHeader category={category} count={list.length} />
            <CategoryFeed articles={list} />
          </>
        ) : (
          <div className="w-full px-4 md:px-8 py-24 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-primary-50 text-primary-500 flex items-center justify-center mb-5">
              <i className="ri-emotion-unhappy-line text-3xl" aria-hidden="true"></i>
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              这个瓜田还没开通
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-500">
              你访问的分类不存在或已调整，先回首页看看今天的其他热瓜吧。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line" aria-hidden="true"></i>
              返回首页
            </Link>
          </div>
        )}
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}